<template>
  <div>
    <div
      class="row edit-button-group"
      v-if="canEdit"
      v-show="files.length>0"
    >
      <button
        id="settings-general-document-admin-edit"
        class="btn btn--orange"
        v-if="!allowEdit"
        @click="enableEdit()"
      >{{ $t('action.edit') }}</button>
      <button
        id="settings-general-document-admin-cancel"
        class="btn btn--darkblue"
        v-if="allowEdit"
        v-hkldreset
        @click="discardChange()"
      >{{ $t('action.cancel') }}</button>
      <button
        id="settings-general-document-admin-save"
        class="btn btn--orange"
        v-if="allowEdit"
        v-hkldreset
        @click="save()"
      >{{ $t('action.save') }}</button>
    </div>
    <div
      id="project-docs-attachments"
      class="edit-attachments"
    >
      <table class="tender-tnc-table">
        <thead class="file-upload-th">
          <th class="number tender-font">
            #
          </th>
          <th class="filename tender-font">
            {{ $t('tender.attachment.document') }}
          </th>
          <th class="description tender-font">
            {{ $t('tender.attachment.description') }}
          </th>
          <th class="description tender-font">
            {{ $t('tender.attachment.effectiveFrom') }}
          </th>
          <th class="description tender-font">
            {{ $t('tender.attachment.effectiveTo') }}
          </th>
          <th class="uploaded-date tender-font">
            {{ $t('tender.attachment.uploadedDate') }}
          </th>
          <th v-if="canDelete" />
        </thead>
        <draggable
          :options="{handle:'.myHandler'}"
          element="tbody"
          :list="pendingUpdateFiles"
        >
          <template v-for="(item, index) in pendingUpdateFiles">
            <tr
              class="set-border-top"
              :key="item.id"
            >
              <td
                class="number tender-font"
                :class="{myHandler: allowEdit}"
              >
                <span class="attachment-index">{{ index + 1 }}</span>
              </td>
              <td
                class="filename tender-font"
                :class="{myHandler: allowEdit}"
              >
                <a
                  style="text-decoration: underline;"
                  @click="downloadFile(item.id)"
                >{{ item.filename }}</a>
              </td>
              <td class="description tender-font">
                <div v-if="allowEdit">
                  <textarea
                    class="autosize file-upload-desc"
                    v-model.lazy="item.description"
                    v-autosize
                  />
                </div>

                <div v-if="!allowEdit">
                  <div class="emphasis description-text">
                    <p>{{ item.description }}</p>
                  </div>
                </div>
              </td>
              <td class="effective-date">
                <DatePicker
                  :disabledPicker="!allowEdit || isDocEffective(item.effectiveDate)"
                  format="DD MMM YYYY"
                  :clearButton="allowEdit && !isDocEffective(item.effectiveDate)"
                  :class="{ 'error-highlight' : !isValidEffectiveFromAndTo && (!item.effectiveDate) && item.endDate }"
                  :disabled="disabledDatesForEffectiveDate(item)"
                  :calendarButton="true"
                  calendarButtonIcon="fa fa-calendar"
                  :value="getDayFromDate(item.effectiveDate)"
                  @selected="val => updateEffectiveDate(val, item)"
                />
              </td>
              <td class="effective-date">
                <DatePicker
                  :disabledPicker="!allowEdit"
                  format="DD MMM YYYY"
                  :clearButton="allowEdit"
                  :disabled="disabledDatesForEndDate(item)"
                  :calendarButton="true"
                  calendarButtonIcon="fa fa-calendar"
                  :value="getDayFromDate(item.endDate)"
                  @selected="val => updateEndDate(val, item)"
                />
              </td>
              <td
                class="uploaded-at"
                style="text-align: right;"
              >
                {{ formatDate(item.createdAt) }}
              </td>
              <td v-if="canDelete && allowEdit">
                <div
                  class="file-upload-delete"
                  @click="deleteFile(index, item)"
                >
                  &#x2715;
                </div>
              </td>
            </tr>
          </template>
        </draggable>
      </table>
      <div
        class="projects-download-all clearfix"
        v-show="canDownloadAll && files.length > 0"
      >
        <button
          class="btn btn--darkblue tender-sticky-button float-left"
          @click="downloadAll(sourceId)"
        >{{ $t('action.download') }} {{ $t('all') }}</button>
        <div class="float-right clearfix">
          <slot name="buttons" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import lodash from 'lodash'
import querystring from 'querystring'
import Vue from 'vue'

import * as xfs from '@/utils/xfs'
import EventBus from '@/utils/eventBus'
import dateUtil from '@/utils/dateUtil'
import DatePicker from '@/controls/DatePicker'
import { aclHelper } from '@/utils/aclHelper'
import { getAttachments, deleteDocument, updateDocuments } from '@/api/docs/request'

export default {
  name: 'EditTerms',

  components: {
    draggable,
    DatePicker,
  },

  props: {
    canEdit: {
      type: Boolean,
      default () { return false },
    },
    canDelete: {
      type: Boolean,
      default () { return false },
    },
    canDownloadAll: {
      type: Boolean,
      default () { return false },
    },
    sourceId: {
      type: String,
      required: true,
    },
    zipName: {
      type: String,
      default: '',
    },
    action: String,
    updateAction: String,
  },
  data () {
    return {
      Vue,
      dateUtil,
      prevSourceId: '',
      allowChangeName: {},
      files: [],
      pendingUpdateFiles: [],
      allowEdit: false,
      aclHelper,
      isValidEffectiveFromAndTo: true,
    }
  },

  computed: {
    isValidEffective () {
      return this.isValidEffectiveFromAndTo
    },
  },

  watch: {
    async sourceId () {
      this.setupListener()
      await this.loadFiles()
    },
    canEdit (val) {
      if (!val) {
        this.discardChange()
      }
    },
  },

  async mounted () {
    this.setupListener()
    await this.loadFiles()
  },

  destroyed () {
    if (this.sourceId) {
      EventBus.$off(`controls.hkldDropzone.newItem.${this.sourceId}`)
    }
  },

  methods: {
    async deleteFile (index, item) {
      const headers = {}
      if (this.action) {
        headers['X-CMS-Action'] = this.action
      }
      const deleted = await deleteDocument(item.id, headers)
      if (deleted) {
        const pendingDelIdx = this.files.findIndex(file => file.id === item.id)
        if (pendingDelIdx >= 0) {
          this.files.splice(pendingDelIdx, 1)
          this.pendingUpdateFiles = lodash.cloneDeep(this.files)
          EventBus.$emit(`controls.hkldDropzone.deleteItem.${this.sourceId}`, item)
        }
      }
    },

    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },

    async downloadAll (sourceId) {
      const filesId = this.files.map(file => file.id)
      const query = querystring.stringify({docsId: filesId})
      await xfs.downloadFile(`/docs/all/${sourceId}?${query}`, this.zipName)
    },

    async updateEffectiveDate (val, item) {
      if (val) {
        item.effectiveDate = new Date(val).setHours(0, 0, 0)
      } else {
        item.effectiveDate = null
      }
    },
    async updateEndDate (val, item) {
      if (val) {
        item.endDate = new Date(val).setHours(0, 0, 0)
      } else {
        item.endDate = null
      }
    },

    formatDate (dateString) {
      return dateUtil.formatDate(dateString, true, true)
    },
    getDayFromDate (dateString) {
      if (dateString) {
        const format = dateUtil.DD_MMM_YYYY
        return dateUtil.format(dateString, format)
      }
    },
    disabledDatesForEffectiveDate (item) {
      const toDate = dateUtil.getTodayStartDateObject()
      if (item.endDate) {
        const fromDate = new Date(item.endDate)
        fromDate.setDate(fromDate.getDate())
        return {
          from: fromDate,
          to: toDate,
        }
      }
      return {
        to: toDate,
      }
    },
    disabledDatesForEndDate (item) {
      let toDate
      if (item.effectiveDate && (new Date(item.effectiveDate) > new Date())) {
        toDate = new Date(item.effectiveDate)
      } else {
        toDate = dateUtil.getTodayStartDateObject()
        toDate.setDate(toDate.getDate())
      }
      return {
        to: toDate,
      }
    },
    isDocEffective (effectiveDate) {
      if (effectiveDate) {
        var now = new Date()
        return new Date(effectiveDate).getTime() < new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()).getTime()
      }
      return false
    },

    async loadFiles () {
      if (!this.sourceId) return
      this.files = await getAttachments(this.sourceId)
      this.pendingUpdateFiles = lodash.cloneDeep(this.files)
      this.$parent.$emit(`controls.hkldDropzone.loadFilesLength`, this.files.length)
    },

    addNewItem () {
      this.loadFiles()
    },

    setupListener () {
      if (this.sourceId) {
        EventBus.$off(`controls.hkldDropzone.newItem.${this.prevSourceId}`)
        EventBus.$on(`controls.hkldDropzone.newItem.${this.sourceId}`, () => {
          this.addNewItem()
        })
        this.prevSourceId = this.sourceId
      }
    },
    enableEdit () {
      this.allowEdit = true
    },

    discardChange () {
      this.pendingUpdateFiles = null
      this.pendingUpdateFiles = lodash.cloneDeep(this.files)
      this.allowEdit = false
    },
    validEffectiveFromAndTo () {
      this.pendingUpdateFiles.forEach((item) => {
        if (item.endDate && !item.effectiveDate) {
          this.isValidEffectiveFromAndTo = false
        }
      })
    },
    async save () {
      this.isValidEffectiveFromAndTo = true
      this.validEffectiveFromAndTo()
      if (!this.isValidEffectiveFromAndTo) {
        return false
      }
      await updateDocuments(this.pendingUpdateFiles, this.updateAction)
      this.files = null
      this.files = lodash.cloneDeep(this.pendingUpdateFiles)
      this.allowEdit = false
    },
  },
}
</script>

<style scoped lang="scss">
@import '~styles/tender/attachments';

pre {
  display: inline-block;
  vertical-align: top;
}

.filename {
  width: 25%;
}

.description {
  width: 50%;
}

.description-text .fa{
  margin-left: 5px;
}

.uploaded-date {
  width: 20%;
  padding: .9rem;
  text-align: right;
  text-transform: uppercase;
}

.myHandler:hover {
  cursor: move;
}

.effective-date .vdp-datepicker {
    border: none;
    border-right: none;
    background-color: transparent;
    padding: 0 0;
}
.edit-button-group {
  float: right;
  width: auto; // quirk fix bug for button float to left randomly
}

.edit-button-group button {
    margin-left: 8px;
    margin-bottom: 15px;
}
.effective-date .error-highlight {
  background-color: #f9ecea;
}

.tender-tnc-table {
  scroll-behavior: smooth;
  thead th {
    position: sticky;
    top: 0px;
    box-shadow: 0 1px 1px -1px rgba(0, 0, 0, 0.4);
    z-index: 1;
    background: white;
    white-space: nowrap;
  }
}
</style>
